import { fg } from '@atlassian/jira-feature-gating';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { safeCloneResource } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { QUERY } from './RovoEntitlementQuery.tsx';

type JiraRovoWithAiDisabledReason =
	| 'NO_ROVO_ENTITLEMENT'
	| 'ROVO_DISABLED_IN_ALL_JIRA_PRODUCTS'
	| 'RBAC_ACCESS_DENIED'
	| 'NO_USER_CONTEXT_AVAILABLE'
	| 'NO_WRITE_PERMS_FOR_ROVO';

export type JiraRovoAiProduct =
	| 'JIRA'
	| 'JIRA_SERVICE_MANAGEMENT'
	| 'JIRA_CUSTOMER_SERVICE'
	| 'JIRA_PRODUCT_DISCOVERY';

type RovoEnablementStatus = {
	isRovoEntitlementEnabledForTenant: boolean | null;
	isRovoAiFeaturesEnabledInAnyJiraProducts: {
		isEnabled: boolean | null;
		disabledReason: JiraRovoWithAiDisabledReason | null;
	} | null;
	rovoAiFeaturesEnabledJiraProducts: JiraRovoAiProduct[] | null;
};

type RovoEntitlementGraphQLResponse = {
	data: {
		jira: {
			isRovoEnabled: boolean;
		} | null;

		jira_isRovoLLMEnabled?: boolean;
		jira_isBetaAiFeaturesEnabled?: boolean;
		jira_rovoEnablementStatus?: RovoEnablementStatus | null;
	} | null;
	errors: Array<{ message?: string }>;
};

const RESOURCE_TYPE_ROVO_ENTITLEMENT = 'ROVO_ENTITLEMENT';

type RovoEntitlementData = {
	isRovoEnabled: boolean | null;
	/**
	 * @deprecated Legacy site-wide Rovo LLM flag. Prefer the product-aware hooks:
	 * `useIsRovoAiEnabledForJiraProduct` / `useIsRovoAiEnabledForProjectType` for product-specific
	 * surfaces, or `useIsRovoAiEnabledInAnyJiraProduct` for common surfaces. They honour per-product
	 * entitlement and fall back to this legacy flag when the enablement status is unavailable.
	 */
	isRovoLLMEnabled: boolean | null | undefined;
	isBetaAiFeaturesEnabled: boolean | null | undefined;
	rovoEnablementStatus: RovoEnablementStatus | null;
};

const fetchRovoEntitlement = async (cloudId: string): Promise<RovoEntitlementData> => {
	try {
		const response = await performPostRequest<RovoEntitlementGraphQLResponse>(
			'/gateway/api/graphql',
			{
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
						shouldIncludeBetaAiFeatures: true,
					},
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		// handle GraphQL errors
		if (response.errors) {
			fireErrorAnalytics({
				meta: {
					id: 'navigationResource',
					packageName: 'jiraRouterResourcesRovoEntitlement',
					teamName: 'ai-mate',
				},
				error: new Error(
					`RovoEntitlement Error. ${response.errors.map((error) => error.message).join('; ')}; IsDataEmpty=${response.data?.jira === null || response.data?.jira === undefined}`,
				),
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		const isRovoEnabled = response.data?.jira?.isRovoEnabled ?? null;
		const isRovoLLMEnabled = response.data?.jira_isRovoLLMEnabled ?? null;
		const isBetaAiFeaturesEnabled = response.data?.jira_isBetaAiFeaturesEnabled ?? null;
		const rovoEnablementStatus = response.data?.jira_rovoEnablementStatus ?? null;

		return {
			isRovoEnabled,
			isRovoLLMEnabled,
			isBetaAiFeaturesEnabled,
			rovoEnablementStatus,
		};
	} catch (error: unknown) {
		fireErrorAnalytics({
			meta: {
				id: 'navigationResource',
				packageName: 'jiraRouterResourcesRovoEntitlement',
				teamName: 'ai-mate',
			},
			error: error instanceof Error ? error : new Error(String(error)),
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	return {
		isRovoEnabled: null,
		isRovoLLMEnabled: undefined,
		isBetaAiFeaturesEnabled: undefined,
		rovoEnablementStatus: null,
	};
};

export const rovoEntitlementResource: RouteResource<RovoEntitlementData> = safeCloneResource(
	createResource<RovoEntitlementData>({
		type: 'ROVO_ENTITLEMENT',
		getKey: () => RESOURCE_TYPE_ROVO_ENTITLEMENT,
		getData: (_, { tenantContext: { cloudId } }) => fetchRovoEntitlement(cloudId),
		maxAge: Infinity,
	}),
	{
		get isPageShellCritical() {
			return !fg('jira_ssr_demote_optional_critical_resources');
		},
	},
);

export const useRovoEntitlementResource = (): UseResourceHookResponse<RovoEntitlementData> =>
	useResource(rovoEntitlementResource);
